import { Grid, IconButton } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { ActionButton } from "../../Button";
import { useLanguageContext } from "../../../contexts/LanguageContext";
import DefaultConstants from "../../../data/Constants";
import useState from "../../../hooks/useState";
import Dialog, { IDefaultDialogProps } from "../../Dialog";
import { ServicePriorityData, ServicePriorityEnum } from "../../../data/Enums";
import SelectField from "../../SelectField";
import { useAuthContext } from "../../../contexts/AuthContext";
import { AttachFile } from '@mui/icons-material';
import { useNotificationContext } from "../../../contexts/NotificationContext";
import FormHelper from "../../../helpers/FormHelper";
import AddAttachmentsDialog from "../AddAttachmentsDialog";
import useDialog from "../../../hooks/useDialog";
import Tooltip from "../../Tooltip";
import RichTextEditor from "../../RichTextEditor";
import Badge from "../../Badge";
import ServiceOrderService from "../../../services/ServiceOrderService";
import { IRoom } from "../../../interfaces/IPartnerPms";
import TagField from "../../TagField";


interface IAddServiceOrderDialog extends IDefaultDialogProps {
    handleListRefresh: () => void;
    room?: IRoom;
}


interface IStateValues {
    description: string;
    priorityId: ServicePriorityEnum;
    departmentId: number;
    attachments: File[];
}


const AddDialog: React.FunctionComponent<IAddServiceOrderDialog> = ({
    open,
    handleClose,
    handleListRefresh,
    room = undefined
}) => {
    const { translate } = useLanguageContext();
    const { showToast } = useNotificationContext();
    const { enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        description: '',
        priorityId: ServicePriorityEnum.Empty,
        departmentId: 0,
        attachments: []
    });

    const addAttachmentsDialog = useDialog();


    useEffect(() => {
        state.reset();
        // eslint-disable-next-line
    }, []);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);

        // Check description
        const descriptionMinimumLength = 20;
        const description = formData.get('description')?.toString() ?? '';
        if (description.length < descriptionMinimumLength) {
            showToast(
                "warning", 
                translate('_addServiceOrderDialogDescriptionMinimumLengthError').replace('{minimumLength}', descriptionMinimumLength.toString())
            );
            return;
        }

        // Check minimum attachments
        const attachments = {
            minimumNumber: enterprise?.serviceOrderMinimumNumberAttachments ?? 0,
            error: '_addServiceOrderDialogEnterpriseMinimumAttachmentsError'
        };

        if (state.values.departmentId > 0) {
            const department = enterprise?.departments.find(d => +d.id === +state.values.departmentId);
            
            if (department !== undefined) {
                attachments.minimumNumber = department.minimumNumberAttachments;
                attachments.error = '_addServiceOrderDialogDepartmentMinimumAttachmentsError';
            }
        }

        if (state.values.attachments.length < attachments.minimumNumber) {
            const message = translate(attachments.error).replace('{minimumNumber}', attachments.minimumNumber.toString());
            showToast("warning", message);
            addAttachmentsDialog.handleOpen();
            return;
        }


        state.values.attachments.forEach((attachment, index) => {
            formData = FormHelper.Add(formData, `attachment[${index}]`, attachment);
        });

        if (room !== undefined) {
            formData = FormHelper.Add(formData, 'roomId', room.id);
        }
        
        ServiceOrderService.Add(formData).then(() => {
            handleClose();
            handleListRefresh();
            state.reset();
        });
        // eslint-disable-next-line
    }, [state.values, room, translate]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate('add')}
            maxWidth='md'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween dialog-addserviceorder'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={5} xs={12}>
                    <SelectField
                        label={translate('department')}
                        name='departmentId'
                        value={state.values.departmentId}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={[{ value: 0, text: translate('undefined') }].concat((enterprise?.departments ?? []).map(d => {
                            return {
                                value: +d.id,
                                text: d.name
                            };
                        }))}
                    />
                </Grid>

                <Grid item md={5} xs={9}>
                    <SelectField
                        label={translate('priority')}
                        name='priorityId'
                        value={state.values.priorityId}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={Object.values(ServicePriorityData).map(s => {
                            return {
                                value: +s.key,
                                text: translate(s.name)
                            };
                        })}
                    />
                </Grid>

                <Grid item md={2} xs={3} className="display-flex alignitems-center justifycontent-flexend" paddingTop={1}>
                    <Tooltip title={translate('attachments')}>
                        <Badge badgeContent={state.values.attachments.length} showZero={true} className='backgroundcolor-primary color-white'>
                            <IconButton size="large" onClick={() => addAttachmentsDialog.handleOpen()} className='borderradius-default backgroundcolor-grey-2 text-color-1'>
                                <AttachFile fontSize="medium" />
                            </IconButton>
                        </Badge>
                    </Tooltip>
                </Grid>

                <Grid item xs={12} marginTop={2}>
                    <TagField
                        label={translate('tags')}
                        name='tag'
                    />
                </Grid>

                <Grid item xs={12} marginTop={2}>
                    <RichTextEditor 
                        label={translate('description')}
                        name="description"
                        value={state.values.description}
                        handleChange={value => state.update('description', value)}
                    />
                </Grid>

                <AddAttachmentsDialog
                    open={addAttachmentsDialog.open}
                    handleClose={() => addAttachmentsDialog.handleClose()}
                    attachments={state.values.attachments}
                    handleUpdateAttachments={(files) => state.update('attachments', files)}
                />
            </Grid>
        </Dialog>
    );
}

export default AddDialog;